// src/components/DealsManagement.jsx
import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import AddDealForm from './AddDealForm'; // ← Import du composant
import Loading from '../utils/Loading';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const DealsManagement = () => {
  const API = useContext(ApiContext);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    recruiter: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalBonus: 0,
    totalDeals: 0
  });

  // ✅ États pour gérer les modals
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [dealToDelete, setDealToDelete] = useState(null);

  // Récupérer les deals
  const fetchDeals = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...filters
      });

      const response = await API.get(`/api/deals?${params}`);
      
      if (response.data.success) {
        setDeals(response.data.deals);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error('Erreur lors du chargement des deals');
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les statistiques de l'entreprise
  const fetchCompanyStats = async () => {
    try {
      const response = await API.get('/api/deals/stats/company');
      if (response.data.success) {
        setStats(response.data.stats.global);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchDeals();
    fetchCompanyStats();
  }, [filters]);

  // Changer le statut d'un deal
  const updateDealStatus = async (dealId, newStatus) => {
    try {
      const response = await API.patch(`/api/deals/${dealId}/status`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success('Statut mis à jour avec succès');
        fetchDeals(pagination.current);
        fetchCompanyStats();
      }
    } catch (error) {
      console.error('Error updating deal status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Ouvrir la confirmation de suppression
  const handleDeleteRequest = (deal) => {
    setPendingDeleteId(deal._id);
    setDealToDelete(deal);
    setShowDeleteConfirm(true);
  };

  // Supprimer un deal
  const deleteDeal = async () => {
    try {
      const response = await API.delete(`/api/deals/${pendingDeleteId}`);

      if (response.data.success) {
        toast.success('Deal supprimé avec succès');
        fetchDeals(pagination.current);
        fetchCompanyStats();
      }
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Erreur lors de la suppression du deal');
    }
  };

  // ✅ Gestionnaires pour les modals
  const handleAddDeal = () => {
    setEditingDeal(null);
    setShowAddForm(true);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setShowAddForm(true);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingDeal(null);
  };

  const handleFormSuccess = () => {
    fetchDeals(pagination.current);
    fetchCompanyStats();
    handleCloseForm();
  };

  // Formatage des montants
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Badge de statut
  const getStatusBadge = (status) => {
    const statusConfig = {
      'en_cours': { color: 'bg-yellow-100 text-yellow-800', label: 'En cours' },
      'valide': { color: 'bg-blue-100 text-blue-800', label: 'Validé' },
      'facture': { color: 'bg-purple-100 text-purple-800', label: 'Facturé' },
      'paye': { color: 'bg-green-100 text-green-800', label: 'Payé' },
      'annule': { color: 'bg-red-100 text-red-800', label: 'Annulé' }
    };

    const config = statusConfig[status] || statusConfig['en_cours'];

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Afficher le loading initial pendant le chargement
  if (loading && deals.length === 0) {
    return (
      <div className="bg-gray-100 min-h-screen flex items-center justify-center pb-36">
        <Loading message="Chargement des deals..." fullHeight={false} />
      </div>
    );
  }

  return (
    <div className=" min-h-screen bg-gray-100">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 m-0">Deals</h1>
          
          {/* ✅ Bouton Nouveau Deal */}
          <button
            onClick={handleAddDeal}
            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 
                     text-white rounded-lg font-medium transition-colors  self-start sm:self-auto"
          >
            <i className="bi bi-plus-lg"></i>
            <span className="hidden sm:inline">Nouveau Deal</span>
            <span className="sm:hidden">Nouveau</span>
          </button>
        </div>
        
        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg border">
            <h3 className="text-sm sm:text-base font-semibold text-slate-600 mb-2">Total Deals</h3>
            <p className="text-2xl sm:text-3xl font-bold text-indigo-600">{stats.totalDeals}</p>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg border">
            <h3 className="text-sm sm:text-base font-semibold text-slate-600 mb-2">CA Total</h3>
            <p className="text-2xl sm:text-3xl font-bold text-green-600">
              {formatCurrency(stats.totalRevenue)}
            </p>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg border">
            <h3 className="text-sm sm:text-base font-semibold text-slate-600 mb-2">Primes Total</h3>
            <p className="text-2xl sm:text-3xl font-bold text-purple-600">
              {formatCurrency(stats.totalBonus)}
            </p>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg border">
            <h3 className="text-sm sm:text-base font-semibold text-slate-600 mb-2">Deal Moyen</h3>
            <p className="text-2xl sm:text-3xl font-bold text-blue-600">
              {formatCurrency(stats.avgDealValue || 0)}
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border mb-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Filtres</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="valide">Validé</option>
              <option value="facture">Facturé</option>
              <option value="paye">Payé</option>
              <option value="annule">Annulé</option>
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Date de début"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Date de fin"
            />

            <button
              onClick={() => {
                setFilters({ status: '', recruiter: '', startDate: '', endDate: '' });
                fetchDeals(1);
              }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-500 hover:bg-slate-600 
                       text-white rounded-lg font-medium transition-colors text-sm"
            >
              <i className="bi bi-arrow-clockwise"></i>
              <span className="hidden sm:inline">Réinitialiser</span>
              <span className="sm:hidden">Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tableau des deals */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg sm:text-xl font-semibold">Liste des Deals</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-slate-600">Chargement...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Deal
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Candidat
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Client
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                    Recruteur
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    CA
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                    Prime
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Statut
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {deals.map((deal) => (
                  <tr key={deal._id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-slate-900">
                          {deal.invoiceNumber}
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(deal.dealDate).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm font-medium text-slate-900 max-w-[120px] truncate">
                        {deal.individual?.Name || 'N/A'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatCurrency(deal.annualSalary)} / an
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm font-medium text-slate-900 max-w-[100px] truncate">
                        {deal.client?.name || 'N/A'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {deal.client?.city || ''}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-sm font-medium text-slate-900">
                        {deal.recruiter?.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-green-600">
                      {formatCurrency(deal.generatedRevenue)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-purple-600 hidden md:table-cell">
                      {formatCurrency(deal.recruiterBonus)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {getStatusBadge(deal.status)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1">
                        {/* Boutons de changement de statut */}
                        {deal.status === 'en_cours' && (
                          <button
                            onClick={() => updateDealStatus(deal._id, 'valide')}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 rounded text-xs bg-blue-50 font-medium transition-colors"
                          >
                            <span className="sm:hidden">✓</span>
                            <span className="hidden sm:inline">Valider</span>
                          </button>
                        )}
                        {deal.status === 'valide' && (
                          <button
                            onClick={() => updateDealStatus(deal._id, 'facture')}
                            className="text-purple-600 hover:text-purple-900 px-2 py-1 rounded text-xs bg-purple-50 font-medium transition-colors"
                          >
                            <span className="sm:hidden">🧾</span>
                            <span className="hidden sm:inline">Facturer</span>
                          </button>
                        )}
                        {deal.status === 'facture' && (
                          <button
                            onClick={() => updateDealStatus(deal._id, 'paye')}
                            className="text-green-600 hover:text-green-900 px-2 py-1 rounded text-xs bg-green-50 font-medium transition-colors"
                          >
                            <span className="sm:hidden">💰</span>
                            <span className="hidden sm:inline">Payé</span>
                          </button>
                        )}
                        
                        <div className="flex space-x-1">
                          {/* ✅ Bouton modifier */}
                          <button
                            onClick={() => handleEditDeal(deal)}
                            className="text-indigo-600 hover:text-indigo-900 px-2 py-1 rounded text-xs bg-indigo-50 transition-colors"
                            title="Modifier le deal"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          
                          {/* Bouton supprimer (sauf si payé) */}
                          {deal.status !== 'paye' && (
                            <button
                              onClick={() => handleDeleteRequest(deal)}
                              className="text-red-600 hover:text-red-900 px-2 py-1 rounded text-xs bg-red-50 transition-colors"
                              title="Supprimer le deal"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs sm:text-sm text-slate-700 text-center sm:text-left">
              <span className="hidden sm:inline">Affichage de {((pagination.current - 1) * 20) + 1} à {Math.min(pagination.current * 20, pagination.total)} sur {pagination.total} résultats</span>
              <span className="sm:hidden">{pagination.total} résultats</span>
            </div>
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => fetchDeals(pagination.current - 1)}
                disabled={!pagination.hasPrev}
                className="px-3 py-1.5 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-sm transition-colors"
              >
                <span className="hidden sm:inline">Précédent</span>
                <span className="sm:hidden">‹</span>
              </button>
              <span className="px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-lg text-sm font-medium">
                <span className="hidden sm:inline">Page {pagination.current} sur {pagination.pages}</span>
                <span className="sm:hidden">{pagination.current}/{pagination.pages}</span>
              </span>
              <button
                onClick={() => fetchDeals(pagination.current + 1)}
                disabled={!pagination.hasNext}
                className="px-3 py-1.5 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-sm transition-colors"
              >
                <span className="hidden sm:inline">Suivant</span>
                <span className="sm:hidden">›</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ✅ Modal AddDealForm */}
      <AddDealForm
        isOpen={showAddForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editDeal={editingDeal}
      />

      {/* Modale de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteDeal}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le deal ${dealToDelete?.invoiceNumber || ''} pour ${dealToDelete?.individual?.Name || 'ce candidat'} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default DealsManagement;